import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Icons } from "@/components/icons";
import { useToast } from "@/components/ui/use-toast";
import useFigmaSessionStore from "@/stores/useFigmaSessionStore";
import { useFigmaAuth } from "@/clients/deflow";
import { useHostAdapter } from "@/clients/host/adapter";

export const Route = createFileRoute("/plugin/figma/onboarding/4")({
  component: OnboardingStep4,
});

function OnboardingStep4() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const { data: figmaAuth, mutate } = useFigmaAuth();
  const { setFigmaSession } = useFigmaSessionStore();
  const adapter = useHostAdapter();
  const [attempts, setAttempts] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);

  const DELAY = 3000;
  const MAX_ATTEMPTS = 20; // 1 min
  const NOTIFY_ATTEMPTS = 5;

  if (!timerRef.current) {
    setTimerRunning(true);
    timerRef.current = setInterval(() => {
      mutate();
      setAttempts((prev) => prev + 1);
    }, DELAY);
  }

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
    setTimerRunning(false);
  };

  useEffect(() => {
    if (attempts > MAX_ATTEMPTS) {
      stopTimer();
    }
  }, [attempts]);

  useEffect(() => {
    if (!figmaAuth?.authorized || !timerRef.current) {
      return;
    }

    stopTimer();

    const figmaTokens = {
      userId: figmaAuth.userId,
      accessToken: figmaAuth.accessToken,
      expiresAt: figmaAuth.expiresAt,
      refreshToken: figmaAuth.refreshToken,
    };

    void adapter.persistHostAuth(figmaTokens, figmaAuth.lastSignIn);

    setFigmaSession({
      lastSignIn: figmaAuth.lastSignIn,
      tokens: figmaTokens,
    });

    console.log({
      page: "OnboardingStep4",
      message: "Account linked, redirecting to /plugin",
    });

    toast({
      title: "Figma account linked",
      description: "You're all set to start publishing your designs",
    });

    navigate({ to: "/plugin/figma", replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [figmaAuth, adapter, setFigmaSession]);

  return (
    <div>
      <div className="px-8 pt-8">
        <div className="w-full">
          <CardHeader>
            <CardTitle className="flex">
              {`We're linking your account`}
              {timerRunning && (
                <Icons.spinner className="ml-2 h-4 w-4 animate-spin" />
              )}
            </CardTitle>
            <CardDescription>
              {!figmaAuth?.authorized &&
                attempts < NOTIFY_ATTEMPTS &&
                "Almost there, just waiting for confirmation from Figma."}
              {!figmaAuth?.authorized &&
                attempts >= NOTIFY_ATTEMPTS &&
                "This seems to be taking a while. Hit Retry to try again."}
              {figmaAuth?.authorized && "Successfully linked account"}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex">
            <Button
              className="mx-auto"
              disabled={attempts < NOTIFY_ATTEMPTS}
              onClick={() => {
                navigate({ to: "/plugin/figma/onboarding/3", replace: true });
              }}
            >
              Retry
            </Button>
          </CardFooter>
        </div>
      </div>
    </div>
  );
}
